import { useState, useEffect, useMemo } from 'react'
import { Modal } from '../base/Modal'
import { Button } from '../base/Button'
import { Input } from '../base/Input'
import { useAuth } from '../../hooks/useAuth'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialMode?: 'login' | 'signup'
}

export const AuthModal = ({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [socialLoading, setSocialLoading] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [usernameError, setUsernameError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [step, setStep] = useState(1)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)

  const { signIn, signUp, signInWithProvider, resetPassword, checkUsernameAvailability } = useAuth()

  useEffect(() => {
    setIsLogin(initialMode === 'login')
    setStep(1)
    setShowForgotPassword(false)
    setResetSuccess(false)
  }, [initialMode])

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: '', color: '' }
    
    let score = 0
    if (password.length >= 8) score++
    if (password.length >= 12) score++
    if (/[a-z]/.test(password)) score++
    if (/[A-Z]/.test(password)) score++
    if (/\d/.test(password)) score++
    if (/[^a-zA-Z0-9]/.test(password)) score++

    if (score <= 2) return { score: 1, label: 'Weak', color: 'bg-red-500' }
    if (score <= 4) return { score: 2, label: 'Medium', color: 'bg-amber-500' }
    return { score: 3, label: 'Strong', color: 'bg-emerald-500' }
  }, [password])

  const validateUsername = (value: string) => {
    if (!value.trim()) {
      setUsernameError('Username is required')
      return false
    }
    if (value.length < 3) {
      setUsernameError('Username must be at least 3 characters')
      return false
    }
    if (value.length > 20) {
      setUsernameError('Username must be less than 20 characters')
      return false
    }
    const usernameRegex = /^[a-zA-Z0-9_-]+$/
    if (!usernameRegex.test(value)) {
      setUsernameError('Only letters, numbers, underscores, and hyphens allowed')
      return false
    }
    if (!/^[a-zA-Z0-9]/.test(value)) {
      setUsernameError('Must start with a letter or number')
      return false
    }
    setUsernameError('')
    return true
  }

  const checkUsername = async (value: string) => {
    if (!validateUsername(value)) return
    setCheckingUsername(true)
    try {
      const isAvailable = await checkUsernameAvailability(value)
      if (!isAvailable) {
        setUsernameError('This username is already taken')
      }
    } catch (err) {
      console.error('Error checking username:', err)
    } finally {
      setCheckingUsername(false)
    }
  }

  const validatePassword = (pass: string, confirmPass: string) => {
    if (!isLogin) {
      if (pass.length < 8) {
        setPasswordError('Password must be at least 8 characters')
        return false
      }
      if (!/(?=.*[a-z])/.test(pass)) {
        setPasswordError('Include at least one lowercase letter')
        return false
      }
      if (!/(?=.*[A-Z])/.test(pass)) {
        setPasswordError('Include at least one uppercase letter')
        return false
      }
      if (!/(?=.*\d)/.test(pass)) {
        setPasswordError('Include at least one number')
        return false
      }
      if (confirmPass && pass !== confirmPass) {
        setPasswordError('Passwords do not match')
        return false
      }
    }
    setPasswordError('')
    return true
  }

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setUsername(value)
    if (value.trim() && !isLogin) {
      const timeoutId = setTimeout(() => {
        checkUsername(value.trim())
      }, 500)
      return () => clearTimeout(timeoutId)
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPassword(value)
    if (!isLogin) {
      validatePassword(value, confirmPassword)
    }
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (!isLogin) {
      validatePassword(password, value)
    }
  }

  const handleNextStep = () => {
    if (step === 1) {
      if (!validateUsername(username.trim())) return
      if (usernameError) return
      setStep(2)
    }
  }

  const handlePrevStep = () => {
    if (step === 2) setStep(1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (isLogin) {
        await signIn(email, password)
        setSuccess('Welcome back!')
        setTimeout(() => {
          onClose()
          resetForm()
        }, 1000)
      } else {
        if (!validateUsername(username.trim())) {
          setLoading(false)
          return
        }
        if (!validatePassword(password, confirmPassword)) {
          setLoading(false)
          return
        }
        if (usernameError) {
          setError('Please fix the username error')
          setLoading(false)
          return
        }
        
        await signUp(email, password, username.trim(), username.trim())
        setSuccess('Account created! Check your email to verify.')
        setTimeout(() => {
          onClose()
          resetForm()
        }, 3000)
      }
    } catch (err: any) {
      console.error('Auth error:', err)
      
      // Provide user-friendly error messages
      let errorMessage = 'An unexpected error occurred. Please try again.'
      
      if (err.message) {
        const msg = err.message.toLowerCase()
        
        if (msg.includes('invalid login credentials') || msg.includes('invalid email or password')) {
          errorMessage = 'Incorrect email or password. Please check your credentials and try again.'
        } else if (msg.includes('email not confirmed')) {
          errorMessage = 'Please verify your email address. Check your inbox for a confirmation link.'
        } else if (msg.includes('user already registered') || msg.includes('already exists')) {
          errorMessage = 'This email is already registered. Please sign in or use a different email.'
        } else if (msg.includes('invalid email')) {
          errorMessage = 'Please enter a valid email address.'
        } else if (msg.includes('password')) {
          errorMessage = 'Password must be at least 6 characters long.'
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = 'Network error. Please check your internet connection and try again.'
        } else if (msg.includes('rate limit')) {
          errorMessage = 'Too many attempts. Please wait a few minutes before trying again.'
        } else {
          // Use the original error message if it's user-friendly
          errorMessage = err.message
        }
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setUsername('')
    setError('')
    setSuccess('')
    setUsernameError('')
    setPasswordError('')
    setIsLogin(initialMode === 'login')
    setStep(1)
    setShowPassword(false)
    setShowConfirmPassword(false)
    setShowForgotPassword(false)
    setResetEmail('')
    setResetLoading(false)
    setResetSuccess(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleModeSwitch = () => {
    setIsLogin(!isLogin)
    setError('')
    setSuccess('')
    setUsernameError('')
    setPasswordError('')
    setStep(1)
    setShowForgotPassword(false)
    setResetSuccess(false)
  }

  const handleSocialLogin = async (provider: 'google' | 'discord') => {
    setSocialLoading(provider)
    setError('')
    try {
      await signInWithProvider(provider)
    } catch (err: any) {
      setError(err.message || `Failed to sign in with ${provider}`)
      setSocialLoading(null)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resetEmail.trim()) {
      setError('Please enter your email address')
      return
    }
    setResetLoading(true)
    setError('')
    try {
      await resetPassword(resetEmail.trim())
      setResetSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.')
    } finally {
      setResetLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setShowForgotPassword(false)
    setResetEmail('')
    setResetSuccess(false)
    setError('')
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="w-full max-w-md mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <i className={`${
              showForgotPassword 
                ? 'ri-lock-unlock-line' 
                : isLogin ? 'ri-login-box-line' : 'ri-user-add-line'
            } text-2xl text-white`}></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {showForgotPassword 
              ? 'Reset Password' 
              : isLogin ? 'Welcome Back' : 'Join DramaHub'}
          </h2>
          <p className="text-gray-500 mt-1 text-sm">
            {showForgotPassword 
              ? 'Enter your email and we\u2019ll send you a reset link'
              : isLogin ? 'Sign in to continue your journey' : 'Create an account to get started'}
          </p>
        </div>

        {/* Forgot Password Flow */}
        {showForgotPassword ? (
          <div>
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-2">
                <i className="ri-error-warning-line text-lg"></i>
                <span className="text-sm">{error}</span>
              </div>
            )}

            {resetSuccess ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-mail-check-line text-2xl text-emerald-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-600 mb-1">
                  We\u2019ve sent a password reset link to
                </p>
                <p className="text-sm font-medium text-gray-900 mb-6">{resetEmail}</p>
                <p className="text-xs text-gray-500 mb-6">
                  Didn\u2019t receive the email? Check your spam folder or try again.
                </p>
                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    onClick={handleBackToLogin}
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Back to Sign In
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setResetSuccess(false); setError(''); }}
                    className="w-full text-sm text-amber-600 hover:text-amber-700 font-medium cursor-pointer py-2"
                  >
                    Try a different email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      autoFocus
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  disabled={resetLoading}
                >
                  {resetLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Reset Link
                      <i className="ri-send-plane-line ml-2"></i>
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full flex items-center justify-center text-sm text-gray-600 hover:text-gray-900 cursor-pointer py-2"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  Back to Sign In
                </button>
              </form>
            )}
          </div>
        ) : (
          <>
            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => handleSocialLogin('google')}
                disabled={!!socialLoading || loading}
                className="w-full flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {socialLoading === 'google' ? (
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                ) : (
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span className="text-sm font-medium text-gray-700">
                  {socialLoading === 'google' ? 'Connecting...' : 'Continue with Google'}
                </span>
              </button>

              <button
                type="button"
                onClick={() => handleSocialLogin('discord')}
                disabled={!!socialLoading || loading}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-[#5865F2] text-white rounded-lg hover:bg-[#4752C4] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {socialLoading === 'discord' ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                ) : (
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617 1.25.077.077 0 0 0-.079.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 12.299 12.299 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {socialLoading === 'discord' ? 'Connecting...' : 'Continue with Discord'}
                </span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or continue with email</span>
              </div>
            </div>

            {/* Progress indicator for signup */}
            {!isLogin && (
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center space-x-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step >= 1 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    1
                  </div>
                  <div className={`w-12 h-1 rounded-full transition-all ${
                    step >= 2 ? 'bg-amber-500' : 'bg-gray-200'
                  }`}></div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step >= 2 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    2
                  </div>
                </div>
              </div>
            )}

            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-2">
                <i className="ri-error-warning-line text-lg"></i>
                <span className="text-sm">{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg flex items-center space-x-2">
                <i className="ri-check-line text-lg"></i>
                <span className="text-sm">{success}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Login Form */}
              {isLogin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-sm font-medium text-gray-700">Password</label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true)
                          setResetEmail(email)
                          setError('')
                          setSuccess('')
                        }}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={handlePasswordChange}
                        required
                        className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Signup Step 1: Username */}
              {!isLogin && step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Choose a Username</label>
                    <div className="relative">
                      <i className="ri-at-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="text"
                        placeholder="your_username"
                        value={username}
                        onChange={handleUsernameChange}
                        required
                        className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm ${
                          usernameError ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {checkingUsername && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      {!checkingUsername && username.length >= 3 && !usernameError && (
                        <i className="ri-check-line absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"></i>
                      )}
                    </div>
                    {usernameError && (
                      <p className="text-xs text-red-600 mt-1 flex items-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {usernameError}
                      </p>
                    )}
                    {!usernameError && username.length >= 3 && !checkingUsername && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center">
                        <i className="ri-check-line mr-1"></i>
                        Username is available!
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      3-20 characters, letters, numbers, underscores, hyphens
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="primary"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    onClick={handleNextStep}
                    disabled={checkingUsername || !!usernameError || username.length < 3}
                  >
                    Continue
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Button>
                </div>
              )}

              {/* Signup Step 2: Email & Password */}
              {!isLogin && step === 2 && (
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex items-center text-sm text-gray-600 hover:text-gray-900 cursor-pointer mb-2"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>
                    Back
                  </button>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        value={password}
                        onChange={handlePasswordChange}
                        required
                        minLength={8}
                        className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm ${
                          passwordError ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {password && (
                      <div className="mt-2">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                              style={{ width: `${(passwordStrength.score / 3) * 100}%` }}
                            ></div>
                          </div>
                          <span className={`text-xs font-medium ${
                            passwordStrength.score === 1 ? 'text-red-600' :
                            passwordStrength.score === 2 ? 'text-amber-600' : 'text-emerald-600'
                          }`}>
                            {passwordStrength.label}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                        className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm ${
                          passwordError ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                    {passwordError && (
                      <p className="text-xs text-red-600 mt-1 flex items-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {passwordError}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    disabled={loading || !!passwordError}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <i className="ri-arrow-right-line ml-2"></i>
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Login Submit Button */}
              {isLogin && (
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Signing In...
                    </>
                  ) : (
                    <>
                      Sign In
                      <i className="ri-arrow-right-line ml-2"></i>
                    </>
                  )}
                </Button>
              )}
            </form>

            {/* Mode Switch */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                {isLogin ? "Don\u2019t have an account?" : 'Already have an account?'}
              </p>
              <button
                type="button"
                onClick={handleModeSwitch}
                className="mt-1 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer"
              >
                {isLogin ? 'Create an account' : 'Sign in instead'}
              </button>
            </div>

            {/* Terms */}
            {!isLogin && (
              <p className="text-xs text-gray-500 text-center mt-4">
                By creating an account, you agree to our{' '}
                <a href="#" className="text-amber-600 hover:underline cursor-pointer">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-amber-600 hover:underline cursor-pointer">Privacy Policy</a>
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

export default AuthModal
